import { useState, useEffect } from 'react';
import SpeechRecognition, {
    useSpeechRecognition,
} from 'react-speech-recognition';

type MicrophonePermission = 'granted' | 'denied' | 'prompt' | 'unsupported';

export const useVoiceInput = () => {
    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition,
    } = useSpeechRecognition();

    const [isActuallyWorking, setIsActuallyWorking] = useState(true);
    const [hasTestedRecording, setHasTestedRecording] = useState(false);
    const [microphonePermission, setMicrophonePermission] =
        useState<MicrophonePermission>('prompt');

    // Debug logging for transcript changes
    useEffect(() => {
        console.log('[VOICE-INPUT] Transcript changed:', transcript);
        console.log('[VOICE-INPUT] Transcript length:', transcript.length);
    }, [transcript]);

    // Debug logging for listening state changes
    useEffect(() => {
        console.log('[VOICE-INPUT] Listening state changed:', listening);
    }, [listening]);

    // Check microphone permissions proactively
    const checkMicrophonePermission = async () => {
        console.log('[VOICE-INPUT] Checking microphone permissions...');

        // First check if browser supports speech recognition
        if (!browserSupportsSpeechRecognition) {
            console.log(
                '[VOICE-INPUT] Browser does not support speech recognition'
            );
            setMicrophonePermission('unsupported');
            return;
        }

        // Try to query microphone permission status
        if (navigator.permissions && navigator.permissions.query) {
            try {
                const permissionStatus = await navigator.permissions.query({
                    name: 'microphone' as PermissionName,
                });

                console.log(
                    '[VOICE-INPUT] Microphone permission status:',
                    permissionStatus.state
                );
                setMicrophonePermission(
                    permissionStatus.state as MicrophonePermission
                );

                // Listen for permission changes
                permissionStatus.onchange = () => {
                    console.log(
                        '[VOICE-INPUT] Microphone permission changed to:',
                        permissionStatus.state
                    );
                    setMicrophonePermission(
                        permissionStatus.state as MicrophonePermission
                    );
                };
            } catch (error) {
                console.log(
                    '[VOICE-INPUT] Could not query microphone permission:',
                    error
                );
                // If we can't query, assume it will prompt on first use
                setMicrophonePermission('prompt');
            }
        } else {
            console.log('[VOICE-INPUT] Permissions API not available');
            // If Permissions API not available, assume prompt on first use
            setMicrophonePermission('prompt');
        }
    };

    // Log browser support on mount and check permissions
    useEffect(() => {
        console.log(
            '[VOICE-INPUT] Browser supports speech recognition:',
            browserSupportsSpeechRecognition
        );
        console.log(
            '[VOICE-INPUT] SpeechRecognition object:',
            SpeechRecognition
        );
        console.log('[VOICE-INPUT] User agent:', navigator.userAgent);

        // Check if running in Brave
        const nav = navigator as Navigator & {
            brave?: { isBrave: () => Promise<boolean> };
        };
        if (nav.brave && nav.brave.isBrave) {
            nav.brave.isBrave().then((isBrave) => {
                console.log('[VOICE-INPUT] Running in Brave browser:', isBrave);
                if (isBrave) {
                    console.warn(
                        '[VOICE-INPUT] Brave browser detected - speech recognition may be blocked by privacy settings'
                    );
                    console.warn(
                        '[VOICE-INPUT] To enable: Settings > Shields > Turn off shields for this site, or Settings > Privacy > Use Google services for push messaging'
                    );
                }
            });
        }

        // Check microphone permissions
        checkMicrophonePermission();
    }, [browserSupportsSpeechRecognition]);

    // Clean up on unmount
    useEffect(() => {
        return () => {
            console.log('[VOICE-INPUT] Cleaning up - listening:', listening);
            if (listening) {
                SpeechRecognition.stopListening();
            }
        };
    }, [listening]);

    const startListening = async () => {
        console.log('[VOICE-INPUT] Starting to listen...');
        resetTranscript();
        setHasTestedRecording(true);

        try {
            // Request microphone access (will trigger browser prompt if needed)
            await navigator.mediaDevices.getUserMedia({ audio: true });
            console.log('[VOICE-INPUT] Microphone access granted');
            setMicrophonePermission('granted');

            await SpeechRecognition.startListening({
                continuous: true,
                language: 'en-US',
            });

            console.log('[VOICE-INPUT] Successfully started listening');

            // Check if it's actually working after 2 seconds
            setTimeout(() => {
                if (listening && !transcript) {
                    console.warn(
                        '[VOICE-INPUT] Speech recognition may not be working - listening but no transcript'
                    );
                }
            }, 2000);
        } catch (error: any) {
            console.error('[VOICE-INPUT] Error starting listening:', error);
            console.error('[VOICE-INPUT] Error details:', {
                name: error.name,
                message: error.message,
                stack: error.stack,
            });

            // Handle permission denial
            if (
                error.name === 'NotAllowedError' ||
                error.name === 'PermissionDeniedError'
            ) {
                console.error('[VOICE-INPUT] Microphone permission denied');
                setMicrophonePermission('denied');
            }

            setIsActuallyWorking(false);
        }
    };

    const stopListening = () => {
        console.log('[VOICE-INPUT] Stopping listening...');
        SpeechRecognition.stopListening();
        console.log('[VOICE-INPUT] Final transcript:', transcript);
    };

    const toggleListening = () => {
        console.log(
            '[VOICE-INPUT] Toggle listening - current state:',
            listening
        );
        if (listening) {
            stopListening();
        } else {
            startListening();
        }
    };

    return {
        transcript,
        listening,
        isSupported:
            browserSupportsSpeechRecognition &&
            isActuallyWorking &&
            microphonePermission !== 'unsupported',
        microphonePermission,
        permissionDenied: microphonePermission === 'denied',
        startListening,
        stopListening,
        toggleListening,
        resetTranscript,
    };
};
